import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Video, Plus, Edit, Trash2, Loader2, Users, Award, Send, Calendar, Clock, ExternalLink, CheckCircle, XCircle, Search, Film } from 'lucide-react';
import { toast } from 'sonner';

const defaultForm = {
  title: '', description: '', thumbnail: '', price: 0, max_participants: 0,
  type: 'live', status: 'draft', meeting_url: '', replay_url: '', recording_url: '',
  speaker_name: '', speaker_bio: '', speaker_photo: '', scheduled_at: '',
  duration_minutes: 60, tags: [], category: '', is_published: false,
};

const defaultSessionForm = {
  title: '', description: '', scheduled_at: '', duration_minutes: 60,
  meeting_url: '', replay_url: '', order: 0,
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  upcoming: 'bg-blue-100 text-blue-800',
  live: 'bg-red-100 text-red-800',
  completed: 'bg-green-100 text-green-800',
};

const statusLabels = {
  draft: 'Draft', upcoming: 'Akan Datang', live: 'Sedang Live', completed: 'Selesai',
};

export default function Webinars() {
  const [tab, setTab] = useState('webinars');
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [sessionForm, setSessionForm] = useState(defaultSessionForm);
  const [editSessionId, setEditSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [regSearch, setRegSearch] = useState('');

  const loadWebinars = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/tenant/webinars');
      setWebinars(data.webinars || []);
    } catch { toast.error('Gagal memuat webinar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWebinars(); }, [loadWebinars]);

  const handleSave = async () => {
    if (!form.title) return toast.error('Judul wajib diisi');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/tenant/webinars/${editId}`, form);
        toast.success('Webinar berhasil diupdate');
      } else {
        await api.post('/tenant/webinars', form);
        toast.success('Webinar berhasil dibuat');
      }
      setShowForm(false); setForm(defaultForm); setEditId(null);
      loadWebinars();
    } catch { toast.error('Gagal menyimpan webinar'); }
    finally { setSaving(false); }
  };

  const handleEdit = (w) => {
    setForm({
      title: w.title || '', description: w.description || '', thumbnail: w.thumbnail || '',
      price: w.price || 0, max_participants: w.max_participants || 0, type: w.type || 'live',
      status: w.status || 'draft', meeting_url: w.meeting_url || '', replay_url: w.replay_url || '', recording_url: w.recording_url || '',
      speaker_name: w.speaker_name || '', speaker_bio: w.speaker_bio || '', speaker_photo: w.speaker_photo || '',
      scheduled_at: w.scheduled_at || '', duration_minutes: w.duration_minutes || 60,
      tags: w.tags || [], category: w.category || '', is_published: w.is_published || false,
    });
    setEditId(w.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus webinar ini?')) return;
    try {
      await api.delete(`/tenant/webinars/${id}`);
      toast.success('Webinar dihapus');
      loadWebinars();
    } catch { toast.error('Gagal menghapus'); }
  };

  // Detail view
  const openDetail = async (w) => {
    setSelectedWebinar(w);
    setTab('peserta');
    try {
      const [regRes, certRes, detailRes] = await Promise.all([
        api.get(`/tenant/webinars/${w.id}/registrations`),
        api.get(`/tenant/webinars/${w.id}/certificates`),
        api.get(`/tenant/webinars/${w.id}`),
      ]);
      setRegistrations(regRes.data.registrations || []);
      setCertificates(certRes.data.certificates || []);
      setSessions(detailRes.data.sessions || []);
    } catch { toast.error('Gagal memuat detail'); }
  };

  const handleMarkAttended = async (regId, attended) => {
    try {
      const endpoint = attended ? 'mark-attended' : 'unmark-attended';
      await api.post(`/tenant/webinars/${selectedWebinar.id}/registrations/${regId}/${endpoint}`);
      setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, attended } : r));
      toast.success(attended ? 'Ditandai hadir' : 'Kehadiran dibatalkan');
    } catch { toast.error('Gagal update kehadiran'); }
  };

  const handleSendReminder = async () => {
    if (!window.confirm('Kirim reminder ke semua peserta?')) return;
    try {
      const { data } = await api.post(`/tenant/webinars/${selectedWebinar.id}/send-reminder`);
      toast.success(`Reminder terkirim ke ${data.sent} peserta`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim reminder');
    }
  };

  const handleGenerateCerts = async () => {
    if (!window.confirm('Generate sertifikat untuk semua peserta yang hadir?')) return;
    try {
      const { data } = await api.post(`/tenant/webinars/${selectedWebinar.id}/generate-certificates`);
      toast.success(`${data.generated} sertifikat berhasil digenerate`);
      const certRes = await api.get(`/tenant/webinars/${selectedWebinar.id}/certificates`);
      setCertificates(certRes.data.certificates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal generate sertifikat');
    }
  };

  const handleSendRecording = async () => {
    if (!window.confirm('Kirim link rekaman webinar ke semua peserta?')) return;
    try {
      const { data } = await api.post(`/tenant/webinars/${selectedWebinar.id}/send-recording`);
      toast.success(`Rekaman terkirim ke ${data.sent} peserta`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim rekaman');
    }
  };

  // Sessions
  const handleSaveSession = async () => {
    if (!sessionForm.title) return toast.error('Judul sesi wajib diisi');
    try {
      if (editSessionId) {
        await api.put(`/tenant/webinars/${selectedWebinar.id}/sessions/${editSessionId}`, sessionForm);
        toast.success('Sesi diupdate');
      } else {
        await api.post(`/tenant/webinars/${selectedWebinar.id}/sessions`, sessionForm);
        toast.success('Sesi ditambahkan');
      }
      setShowSessionForm(false); setSessionForm(defaultSessionForm); setEditSessionId(null);
      const { data } = await api.get(`/tenant/webinars/${selectedWebinar.id}`);
      setSessions(data.sessions || []);
    } catch { toast.error('Gagal menyimpan sesi'); }
  };

  const handleDeleteSession = async (sid) => {
    if (!window.confirm('Hapus sesi ini?')) return;
    try {
      await api.delete(`/tenant/webinars/${selectedWebinar.id}/sessions/${sid}`);
      setSessions(prev => prev.filter(s => s.id !== sid));
      toast.success('Sesi dihapus');
    } catch { toast.error('Gagal menghapus sesi'); }
  };

  const filteredRegs = registrations.filter(r =>
    !regSearch || r.participant_name?.toLowerCase().includes(regSearch.toLowerCase()) ||
    r.participant_email?.toLowerCase().includes(regSearch.toLowerCase())
  );

  if (selectedWebinar) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedWebinar(null); setTab('webinars'); }}>← Kembali</Button>
            <h2 className="text-xl font-bold mt-1">{selectedWebinar.title}</h2>
            <p className="text-sm text-slate-500">{selectedWebinar.speaker_name} • {selectedWebinar.scheduled_at || '-'}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSendReminder}><Send size={14} className="mr-1" /> Kirim Reminder</Button>
            <Button variant="outline" size="sm" onClick={handleSendRecording} className="text-violet-700 border-violet-300 hover:bg-violet-50"><Film size={14} className="mr-1" /> Kirim Rekaman</Button>
            <Button variant="outline" size="sm" onClick={handleGenerateCerts}><Award size={14} className="mr-1" /> Generate Sertifikat</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="peserta">Peserta ({registrations.length})</TabsTrigger>
            <TabsTrigger value="sesi">Sesi ({sessions.length})</TabsTrigger>
            <TabsTrigger value="sertifikat">Sertifikat ({certificates.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="peserta">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Daftar Peserta</CardTitle>
                  <div className="relative w-64">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <Input placeholder="Cari peserta..." value={regSearch} onChange={e => setRegSearch(e.target.value)} className="pl-8 h-9" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredRegs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">Belum ada peserta terdaftar</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left">
                        <th className="pb-2 font-medium">Nama</th>
                        <th className="pb-2 font-medium">Email</th>
                        <th className="pb-2 font-medium">Phone</th>
                        <th className="pb-2 font-medium">Pembayaran</th>
                        <th className="pb-2 font-medium">Hadir</th>
                        <th className="pb-2 font-medium">Aksi</th>
                      </tr></thead>
                      <tbody>
                        {filteredRegs.map(r => (
                          <tr key={r.id} className="border-b hover:bg-slate-50">
                            <td className="py-2">{r.participant_name}</td>
                            <td className="py-2">{r.participant_email}</td>
                            <td className="py-2">{r.participant_phone || '-'}</td>
                            <td className="py-2">
                              <Badge variant={r.payment_status === 'paid' || r.payment_status === 'free' ? 'default' : 'secondary'}>
                                {r.payment_status === 'free' ? 'Gratis' : r.payment_status === 'paid' ? 'Lunas' : 'Pending'}
                              </Badge>
                            </td>
                            <td className="py-2">
                              {r.attended ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-slate-300" />}
                            </td>
                            <td className="py-2">
                              <Button size="sm" variant={r.attended ? 'secondary' : 'default'} onClick={() => handleMarkAttended(r.id, !r.attended)}>
                                {r.attended ? 'Batalkan' : 'Hadir'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sesi">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Sesi Webinar</CardTitle>
                  <Button size="sm" onClick={() => { setSessionForm(defaultSessionForm); setEditSessionId(null); setShowSessionForm(true); }}>
                    <Plus size={14} className="mr-1" /> Tambah Sesi
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">Belum ada sesi. Tambahkan sesi untuk webinar series.</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((s, i) => (
                      <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">Sesi {i + 1}: {s.title}</p>
                          <p className="text-xs text-slate-500">{s.scheduled_at || '-'} • {s.duration_minutes} menit</p>
                          {s.meeting_url && <p className="text-xs text-blue-500 truncate max-w-md">{s.meeting_url}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setSessionForm(s); setEditSessionId(s.id); setShowSessionForm(true); }}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteSession(s.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sertifikat">
            <Card>
              <CardHeader><CardTitle className="text-base">Sertifikat</CardTitle></CardHeader>
              <CardContent>
                {certificates.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">Belum ada sertifikat. Klik "Generate Sertifikat" setelah menandai kehadiran peserta.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left">
                        <th className="pb-2 font-medium">No. Sertifikat</th>
                        <th className="pb-2 font-medium">Nama</th>
                        <th className="pb-2 font-medium">Email</th>
                        <th className="pb-2 font-medium">Tanggal</th>
                      </tr></thead>
                      <tbody>
                        {certificates.map(c => (
                          <tr key={c.id} className="border-b hover:bg-slate-50">
                            <td className="py-2 font-mono text-xs">{c.certificate_number}</td>
                            <td className="py-2">{c.user_name}</td>
                            <td className="py-2">{c.user_email}</td>
                            <td className="py-2">{c.created_at ? new Date(c.created_at).toLocaleDateString('id-ID') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Session Form Dialog */}
        <Dialog open={showSessionForm} onOpenChange={setShowSessionForm}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editSessionId ? 'Edit Sesi' : 'Tambah Sesi'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Judul Sesi</Label><Input value={sessionForm.title} onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })} /></div>
              <div><Label>Deskripsi</Label><Textarea value={sessionForm.description} onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Jadwal</Label><Input type="datetime-local" value={sessionForm.scheduled_at} onChange={e => setSessionForm({ ...sessionForm, scheduled_at: e.target.value })} /></div>
                <div><Label>Durasi (menit)</Label><Input type="number" value={sessionForm.duration_minutes} onChange={e => setSessionForm({ ...sessionForm, duration_minutes: parseInt(e.target.value) || 60 })} /></div>
              </div>
              <div><Label>Link Meeting</Label><Input value={sessionForm.meeting_url} onChange={e => setSessionForm({ ...sessionForm, meeting_url: e.target.value })} placeholder="https://zoom.us/j/... atau https://meet.google.com/..." /></div>
              <div><Label>Link Replay</Label><Input value={sessionForm.replay_url} onChange={e => setSessionForm({ ...sessionForm, replay_url: e.target.value })} placeholder="URL replay (opsional)" /></div>
              <div><Label>Urutan</Label><Input type="number" value={sessionForm.order} onChange={e => setSessionForm({ ...sessionForm, order: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSessionForm(false)}>Batal</Button>
              <Button onClick={handleSaveSession}>{editSessionId ? 'Update' : 'Simpan'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Video size={24} /> Webinar</h1>
          <p className="text-sm text-slate-500">Kelola webinar live, replay, dan peserta</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditId(null); setShowForm(true); }}>
          <Plus size={16} className="mr-1" /> Buat Webinar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} /></div>
      ) : webinars.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Video size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Belum ada webinar. Buat webinar pertama Anda!</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {webinars.map(w => (
            <Card key={w.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(w)}>
              <CardContent className="p-4">
                {w.thumbnail && <img src={w.thumbnail} alt="" className="w-full h-36 object-cover rounded-lg mb-3" />}
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-sm line-clamp-2 flex-1">{w.title}</h3>
                  <Badge className={`ml-2 text-xs ${statusColors[w.status] || ''}`}>{statusLabels[w.status] || w.status}</Badge>
                </div>
                {w.speaker_name && <p className="text-xs text-slate-500 mb-1">🎤 {w.speaker_name}</p>}
                {w.scheduled_at && <p className="text-xs text-slate-500 mb-1"><Calendar size={12} className="inline mr-1" />{w.scheduled_at}</p>}
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Users size={12} /> {w.registration_count || 0}{w.max_participants > 0 ? `/${w.max_participants}` : ''} peserta</span>
                  <span>{w.price > 0 ? `Rp ${w.price.toLocaleString('id-ID')}` : 'Gratis'}</span>
                </div>
                <div className="flex gap-1 mt-3" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEdit(w)}><Edit size={12} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(w.id)}><Trash2 size={12} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Webinar Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Webinar' : 'Buat Webinar Baru'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul Webinar *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Contoh: Strategi Digital Marketing 2026" /></div>
            <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Deskripsi webinar..." /></div>
            <div><Label>Thumbnail URL</Label><Input value={form.thumbnail} onChange={e => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://..." /></div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipe</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="recorded">Recorded</SelectItem>
                    <SelectItem value="hybrid">Hybrid (Live + Replay)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="upcoming">Akan Datang</SelectItem>
                    <SelectItem value="live">Sedang Live</SelectItem>
                    <SelectItem value="completed">Selesai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Harga (0 = Gratis)</Label><Input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Maks Peserta (0 = Unlimited)</Label><Input type="number" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: parseInt(e.target.value) || 0 })} /></div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Jadwal</Label><Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} /></div>
              <div><Label>Durasi (menit)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} /></div>
            </div>

            <div><Label>Link Meeting (Zoom/GMeet)</Label><Input value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://zoom.us/j/... atau https://meet.google.com/..." /></div>
            <div><Label>Link Replay</Label><Input value={form.replay_url} onChange={e => setForm({ ...form, replay_url: e.target.value })} placeholder="URL replay setelah webinar selesai (opsional)" /></div>
            <div><Label>Link Rekaman</Label><Input value={form.recording_url} onChange={e => setForm({ ...form, recording_url: e.target.value })} placeholder="URL rekaman webinar untuk dikirim ke peserta (opsional)" /></div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-sm mb-3">Info Pembicara</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nama Pembicara</Label><Input value={form.speaker_name} onChange={e => setForm({ ...form, speaker_name: e.target.value })} /></div>
                <div><Label>Foto Pembicara URL</Label><Input value={form.speaker_photo} onChange={e => setForm({ ...form, speaker_photo: e.target.value })} placeholder="https://..." /></div>
              </div>
              <div className="mt-3"><Label>Bio Pembicara</Label><Textarea value={form.speaker_bio} onChange={e => setForm({ ...form, speaker_bio: e.target.value })} rows={2} /></div>
            </div>

            <div><Label>Kategori</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Contoh: Marketing, Bisnis, Teknologi" /></div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} />
              <Label>Publikasikan (tampil di storefront)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Batal</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
              {editId ? 'Update' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
